import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Crop, LoaderCircle, X } from 'lucide-react';
import { Colonia, CropData } from '../../types/colonia';
import { updateAdminInscricao } from './adminData';
import { gerarCarteirinhaDataUrl } from './carteirinhaImage';
import PhotoCropper from './PhotoCropper';

const MAX_TILT = 10;
/** Giro de entrada no mesmo eixo (rotateY) e com a mesma sensação do flip manual. */
const GIRO_ENTRADA_Y = 180;

interface CarteirinhaViewerProps {
  inscricao: Colonia;
  faceInicial: 'frente' | 'verso';
  /** Retângulo de onde o clique partiu (a miniatura na lista), para animar até o centro. */
  origem?: DOMRect | null;
  onClose: () => void;
}

const CarteirinhaViewer: React.FC<CarteirinhaViewerProps> = ({ inscricao, faceInicial, origem, onClose }) => {
  const [localInscricao, setLocalInscricao] = useState(inscricao);
  const [cropMode, setCropMode] = useState<'frente' | 'verso' | null>(null);
  const [imagens, setImagens] = useState({ frente: '', verso: '' });
  const [flipped, setFlipped] = useState(faceInicial === 'verso');
  const tiltRef = useRef<HTMLDivElement>(null);
  const voarRef = useRef<HTMLDivElement>(null);
  // Só o mais recente movimento importa; aplicado no máximo 1x por frame para não sobrecarregar.
  const pendente = useRef<{ rx: number; ry: number; px: number; py: number } | null>(null);
  const agendado = useRef(false);

  // Anima do ponto onde o cartão foi clicado (miniatura) até o centro, girando no mesmo eixo do
  // flip manual (rotateY) — não é um corte seco.
  useLayoutEffect(() => {
    const el = voarRef.current;
    if (!el || !origem) return;
    const destino = el.getBoundingClientRect();
    if (!destino.width || !destino.height) return;
    const escalaX = origem.width / destino.width;
    const escalaY = origem.height / destino.height;
    const dx = origem.left + origem.width / 2 - (destino.left + destino.width / 2);
    const dy = origem.top + origem.height / 2 - (destino.top + destino.height / 2);
    el.style.transition = 'none';
    el.style.opacity = '0';
    el.style.transform = `translate(${dx}px, ${dy}px) scale(${escalaX}, ${escalaY}) perspective(1400px) rotateY(${GIRO_ENTRADA_Y}deg)`;
    void el.offsetWidth; // força reflow antes de animar
    requestAnimationFrame(() => {
      el.style.transition = 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease';
      el.style.opacity = '1';
      el.style.transform = 'translate(0, 0) scale(1, 1) perspective(1400px) rotateY(0deg)';
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gera a face que vai aparecer primeiro (mostra assim que pronta) e só depois a outra, em segundo
  // plano — e adia o trabalho pesado do canvas um frame para o loading/voo já estarem na tela,
  // pra não parecer que o sistema travou.
  useEffect(() => {
    let active = true;
    setImagens({ frente: '', verso: '' });
    (async () => {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (!active) return;
      const principal = await gerarCarteirinhaDataUrl(localInscricao, faceInicial);
      if (!active) return;
      if (principal) setImagens((atual) => ({ ...atual, [faceInicial]: principal }));

      const outraFace = faceInicial === 'frente' ? 'verso' : 'frente';
      const outra = await gerarCarteirinhaDataUrl(localInscricao, outraFace);
      if (!active) return;
      if (outra) setImagens((atual) => ({ ...atual, [outraFace]: outra }));
    })();
    return () => {
      active = false;
    };
  }, [localInscricao, faceInicial]);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [onClose]);

  const aplicarNoProximoFrame = () => {
    if (agendado.current) return;
    agendado.current = true;
    requestAnimationFrame(() => {
      agendado.current = false;
      const el = tiltRef.current;
      const valores = pendente.current;
      if (!el || !valores) return;
      el.style.setProperty('--rx', `${valores.rx}deg`);
      el.style.setProperty('--ry', `${valores.ry}deg`);
      el.style.setProperty('--px', `${valores.px}%`);
      el.style.setProperty('--py', `${valores.py}%`);
    });
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const el = tiltRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width;
    const py = (event.clientY - rect.top) / rect.height;
    pendente.current = {
      rx: (0.5 - py) * MAX_TILT,
      ry: (px - 0.5) * MAX_TILT,
      px: px * 100,
      py: py * 100
    };
    aplicarNoProximoFrame();
  };

  const handleMouseLeave = () => {
    pendente.current = { rx: 0, ry: 0, px: 50, py: 50 };
    aplicarNoProximoFrame();
  };

  const handleSaveCrop = async (cropData: CropData) => {
    if (!cropMode || !localInscricao.id) return;
    const patch = cropMode === 'frente' ? { fotoCrop: cropData } : { fotoCropRetirada: cropData };
    try {
      await updateAdminInscricao(localInscricao.id, patch);
      setLocalInscricao((prev) => ({ ...prev, ...patch }));
      setCropMode(null);
    } catch (err) {
      console.error('Erro ao salvar o recorte', err);
      alert('Erro ao salvar o recorte. Tente novamente.');
    }
  };

  const pronta = faceInicial === 'frente' ? Boolean(imagens.frente) : Boolean(imagens.verso);

  return (
    <div className="carteirinha-viewer-backdrop" role="presentation" onClick={onClose}>
      <div className="carteirinha-viewer-stage" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="carteirinha-viewer-close" onClick={onClose} aria-label="Fechar">
          <X size={22} />
        </button>
        <p className="carteirinha-viewer-hint">
          {!pronta && <LoaderCircle size={15} className="admin-spin" />}
          {pronta ? 'Clique no cartão para virar' : 'Gerando cartão...'}
        </p>
        <div ref={voarRef} className="carteirinha-viewer-flight">
          <div
            ref={tiltRef}
            className="carteirinha-viewer-tilt"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={() => setFlipped((atual) => !atual)}
          >
            <div className={`carteirinha-viewer-flip${flipped ? ' flipped' : ''}`}>
              <div className="carteirinha-viewer-face front">
                {imagens.frente ? (
                  <img src={imagens.frente} alt={`Carteirinha (frente) de ${inscricao.nomeCompleto}`} />
                ) : (
                  <div className="carteirinha-viewer-loading" />
                )}
                <div className="carteirinha-viewer-shine" />
              </div>
              <div className="carteirinha-viewer-face back">
                {imagens.verso ? (
                  <img src={imagens.verso} alt={`Carteirinha (verso) de ${inscricao.nomeCompleto}`} />
                ) : (
                  <div className="carteirinha-viewer-loading" />
                )}
                <div className="carteirinha-viewer-shine" />
              </div>
            </div>
          </div>
        </div>

        {pronta && (
          <button
            type="button"
            className="absolute bottom-4 right-4 flex items-center gap-2 px-3 py-2 bg-black/60 hover:bg-black/80 text-white rounded-lg text-sm transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              setCropMode(flipped ? 'verso' : 'frente');
            }}
          >
            <Crop size={16} />
            Ajustar Recorte
          </button>
        )}

        {cropMode && (
          <PhotoCropper
            imageUrl={(cropMode === 'frente' ? localInscricao.fotoUrl : localInscricao.fotoUrlRetirada) || ''}
            aspect={cropMode === 'frente' ? 436 / 503 : 343 / 446}
            onSave={handleSaveCrop}
            onCancel={() => setCropMode(null)}
          />
        )}
      </div>
    </div>
  );
};

export default CarteirinhaViewer;
